import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, articles, faqs } from '@/mocks/articles';

const heroImage = 'https://readdy.ai/api/search-image?query=Professional%20law%20office%20library%20with%20wooden%20bookshelves%20filled%20with%20legal%20books%2C%20warm%20ambient%20lighting%2C%20scales%20of%20justice%20statue%20on%20desk%2C%20elegant%20dark%20wood%20furniture%2C%20classic%20law%20firm%20aesthetic%2C%20clean%20composition%2C%20soft%20bokeh%20background&width=1600&height=900&seq=hero-legal-2026&orientation=landscape';

export default function Home() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [newsletterMsg, setNewsletterMsg] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setNewsletterStatus('success');
      setNewsletterMsg('订阅成功！感谢您的关注。');
      return;
    }
    formData.delete('website_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4cot3pcjqs2fcslt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setNewsletterStatus('success');
        setNewsletterMsg('订阅成功！感谢您的关注。');
        setNewsletterEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后重试。';
        if (serverMsg.includes('spam')) {
          setNewsletterStatus('success');
          setNewsletterMsg('订阅成功！感谢您的关注。');
        } else {
          setNewsletterStatus('error');
          setNewsletterMsg(serverMsg);
        }
      }
    } catch {
      setNewsletterStatus('error');
      setNewsletterMsg('网络错误，请稍后重试。');
    }
  };

  const featuredArticles = articles.slice(0, 6);

  return (
    <>
      <SchemaOrg type="WebSite" data={{
        name: '强奸罪法律解析与辩护指南',
        url: typeof window !== 'undefined' ? window.location.origin : '',
        description: '专业刑事辩护律师团队提供强奸罪法律解析、构成要件、量刑标准、辩护策略及司法实践案例，帮助当事人了解法律权利。',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: '/search?q={search_term_string}' },
          'query-input': 'required name=search_term_string',
        },
      }} />
      <SchemaOrg type="Organization" data={{
        name: '强奸罪法律解析与辩护指南',
        url: typeof window !== 'undefined' ? window.location.origin : '',
        description: '专业刑事辩护律师团队',
        contactPoint: { '@type': 'ContactPoint', telephone: '400-888-9999', contactType: 'customer service' },
      }} />
      <SchemaOrg type="FAQPage" data={{
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }} />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src={heroImage}
            alt="强奸罪法律解析与辩护指南"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 w-full text-center px-4 md:px-8 max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-4 md:mb-6">
              强奸罪法律解析与辩护指南
            </h1>
            <p className="text-sm md:text-lg text-white/80 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
              围绕强奸罪提供全面法律解析，涵盖法律定义、构成要件、量刑标准、辩护策略及司法实践案例，帮助当事人及家属了解法律权利，寻求专业刑事辩护律师协助
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/legal-definition"
                className="px-6 py-3 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解法律定义
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-md text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
              >
                咨询专业律师
              </Link>
            </div>
          </div>
        </section>

        {/* Category Grid */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">内容分类导航</h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">六大内容板块，全面覆盖强奸罪法律知识的各个维度，助您系统了解相关法律信息</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-1.5 whitespace-nowrap">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed mb-3">{cat.description}</p>
                  <span className="text-xs text-primary-600 font-medium whitespace-nowrap">{cat.articleCount} 篇文章 →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100/70">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10 md:mb-14">
              <div>
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">精选文章</h2>
                <p className="text-sm text-foreground-500">深入解读强奸罪法律要点，掌握核心法律知识</p>
              </div>
              <Link
                to="/category/legal-definition"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {featuredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer flex flex-col"
                >
                  <span className="inline-block px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-3 w-fit whitespace-nowrap">
                    {article.category}
                  </span>
                  <h3 className="font-heading font-semibold text-foreground-950 text-sm md:text-base mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed mb-4 line-clamp-2 flex-1">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishedAt}</span>
                    <span>{article.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/legal-definition"
                className="inline-flex items-center gap-1 text-sm text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">常见法律问题</h2>
              <p className="text-sm text-foreground-500">关于强奸罪，您可能关心的法律问题解答</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group rounded-lg border border-background-200/70 bg-background-50">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-45 transition-transform">
                      <i className="ri-add-line"></i>
                    </span>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-14 md:py-20 bg-background-100/70">
          <div className="w-full px-4 md:px-8 max-w-xl mx-auto text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-mail-send-line text-xl"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">订阅法律资讯</h2>
            <p className="text-sm text-foreground-500 mb-6">第一时间获取最新法律动态与专业解析文章</p>
            <form
              onSubmit={handleNewsletterSubmit}
              data-readdy-form
              className="flex flex-col sm:flex-row gap-2"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none"
                style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
              />
              <input
                type="email"
                name="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="请输入您的邮箱地址"
                className="flex-1 px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </form>
            {newsletterStatus !== 'idle' && (
              <p className={`mt-3 text-xs ${newsletterStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                {newsletterMsg}
              </p>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-foreground-950"></div>
          <div className="relative z-10 w-full px-4 md:px-8 max-w-2xl mx-auto text-center">
            <h2 className="text-xl md:text-3xl font-heading font-bold text-background-50 mb-4">需要专业刑事辩护帮助？</h2>
            <p className="text-sm text-background-50/70 mb-8 leading-relaxed">
              我们的专业刑事辩护律师团队拥有丰富的实务经验，为您提供全面、专业、高效的法律服务。立即联系我们，获取免费初步法律咨询。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/about"
                className="px-6 py-3 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解律师团队
              </Link>
              <a
                href="tel:4008889999"
                className="px-6 py-3 bg-background-50/10 backdrop-blur-sm text-background-50 border border-background-50/20 rounded-md text-sm font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-1"></i> 400-888-9999
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}